import React, { useState, useEffect, useRef } from 'react';
import './vaishno-devi-package.scss';
import { Container, Grid } from '@mui/material';
import SEO from '../../seo/SEO';
import {
  LocationOn,
  Schedule,
  Search,
  KeyboardArrowDown,
  CheckCircle,
  Check,
} from '@mui/icons-material';

const departureCities = [
  'New Delhi',
  'Jammu',
  'Katra',
  'Mumbai',
  'Ahmedabad',
  'Bangalore',
  'Kolkata',
  'Hyderabad',
  'Chandigarh',
];

const durationOptions = [
  { value: 'all', label: 'Any duration' },
  { value: '1d', label: '1 Day (SDR)' },
  { value: '2d', label: '2 Days / 1 Night' },
  { value: '3d', label: '3 Days / 2 Nights' },
];

function CustomDropdown({ icon: Icon, value, options, onChange, placeholder }) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const selectedOption = options.find((opt) =>
    typeof opt === 'object' ? opt.value === value : opt === value
  );

  const displayLabel = selectedOption
    ? typeof selectedOption === 'object'
      ? selectedOption.label
      : selectedOption
    : placeholder || 'Select';

  const handleSelect = (option) => {
    const val = typeof option === 'object' ? option.value : option;
    onChange(val);
    setIsOpen(false);
  };

  return (
    <div className={`customDropdown ${isOpen ? 'open' : ''}`} ref={dropdownRef}>
      <button
        type="button"
        className="dropdownTrigger"
        onClick={() => setIsOpen((prev) => !prev)}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
      >
        {Icon && <Icon className="fieldIcon" />}
        <span className="selectedText">{displayLabel}</span>
        <KeyboardArrowDown className={`arrowIcon ${isOpen ? 'rotated' : ''}`} />
      </button>

      {isOpen && (
        <div className="dropdownMenu" role="listbox">
          {options.map((option, idx) => {
            const optVal = typeof option === 'object' ? option.value : option;
            const optLabel = typeof option === 'object' ? option.label : option;
            const isSelected = optVal === value;

            return (
              <div
                key={idx}
                role="option"
                aria-selected={isSelected}
                className={`dropdownItem ${isSelected ? 'selected' : ''}`}
                onClick={() => handleSelect(option)}
              >
                <span className="itemLabel">{optLabel}</span>
                {isSelected && <Check className="checkIcon" />}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function VaishnoDeviPackage() {
  const [departureCity, setDepartureCity] = useState('New Delhi');
  const [selectedDuration, setSelectedDuration] = useState('all');

  // Enquiry form state
  const [enquiryName, setEnquiryName] = useState('');
  const [enquiryPhone, setEnquiryPhone] = useState('');
  const [enquiryPackage, setEnquiryPackage] = useState('Vaishno Devii - Bhakti Darshan');
  const [enquiryNotes, setEnquiryNotes] = useState('');
  const [enquirySubmitted, setEnquirySubmitted] = useState(false);
  const [enquiryError, setEnquiryError] = useState('');

  // FAQ state
  const [openFaq, setOpenFaq] = useState(null);

  const faqs = [
    {
      id: 1,
      question: 'Is Katra the starting point of pilgrimage?',
      answer:
        'Yes, Katra serves as the base camp and official starting point for the trek to the holy shrine of Mata Vaishno Devi. All yatra registrations, trek routes, battery car bookings, and helicopter flights originate from Katra.',
    },
    {
      id: 2,
      question: 'What is included in a Mata Vaishno Devi package?',
      answer:
        'A comprehensive Katra tour package typically includes hotel accommodation in Katra, pick-up and drop-off transfers, yatra assistance, sightseeing, meals as specified, and optional helicopter or battery-car tickets.',
    },
    {
      id: 3,
      question: 'Are helicopter packages available?',
      answer:
        'Yes, dedicated helicopter packages include tickets for the flight between Katra and Sanjichhat, significantly reducing trek time and making the yatra smooth and convenient for families and senior citizens.',
    },
    {
      id: 4,
      question: 'Why is Katra important for pilgrims?',
      answer:
        'Katra is the focal base town where pilgrims register for their Yatra Parchi/RFID cards, access hotel lodging, lockers, medical facilities, local transport, and prepare for the 12 km trek upwards to Bhawan.',
    },
    {
      id: 5,
      question: 'Is the yatra suitable for families?',
      answer:
        'Yes, the yatra track is fully paved, lighted round the clock, covered with weather sheds, and supported by resting points, food courts, medical aid, and transport alternatives like ponies, palanquins, battery cars, and helicopters.',
    },
  ];

  const toggleFaq = (id) => {
    setOpenFaq((prev) => (prev === id ? null : id));
  };

  const katraPackages = [
    {
      id: 1,
      recommended: true,
      stayTag: '1N Katra',
      image: '/assets/vaishno-packages/package-1.webp',
      meta: '2 NIGHTS · GROUP DEPARTURE · PILGRIMAGE',
      title: 'Vaishno Devi – Bhakti Darshan',
      desc: 'Darshan at Vaishno Devi Bhawan on foot',
      inclusions: ['Hotel', 'Sightseeing', 'Transfer', 'Meals'],
      oldPrice: '₹ 11,999',
      price: '₹ 5,299',
      priceNote: 'Per person on twin sharing',
    },
    {
      id: 2,
      recommended: false,
      stayTag: '1N Katra',
      image: '/assets/vaishno-packages/package-2.webp',
      meta: '1 NIGHT · GROUP DEPARTURE · PILGRIMAGE',
      title: 'Bhakti Darshan with Helicopter',
      desc: 'Helicopter assistance for senior citizens & families',
      inclusions: ['Hotel', 'Sightseeing', 'Transfer', 'Meals'],
      oldPrice: '₹ 13,999',
      price: '₹ 11,991',
      priceNote: 'Per person on twin sharing',
    },
    {
      id: 3,
      recommended: true,
      stayTag: '1N Katra',
      image: '/assets/vaishno-packages/package-3.webp',
      meta: '1 NIGHT · GROUP DEPARTURE · PILGRIMAGE',
      title: 'Vaishno Devi with Helicopter',
      desc: 'Helicopter tickets (Katra – Sanjichhat – Katra)',
      inclusions: ['Hotel', 'Sightseeing', 'Flight', 'Meals'],
      oldPrice: '₹ 21,499',
      price: '₹ 12,999',
      priceNote: 'Per person on twin sharing',
    },
  ];

  const handleBannerSearch = () => {
    const el = document.getElementById('katraPackagesSection');
    if (el) {
      el.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handleBookNow = (title) => {
    setEnquiryPackage(title);
    setEnquirySubmitted(false);
    const el = document.getElementById('enquirySection');
    if (el) {
      el.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handleEnquirySubmit = (e) => {
    e.preventDefault();
    if (!enquiryName.trim() || !enquiryPhone.trim()) {
      setEnquiryError('Please enter your name and mobile number.');
      return;
    }
    setEnquiryError('');
    setEnquirySubmitted(true);
  };

  return (
    <div className="vaishnoPackagePage">
      {/* ==========================================
          HERO BANNER
      ========================================== */}
      <SEO pageKey="vaishnoDeviPackage" />
      <section className="vdpHero">
        <Container maxWidth="lg">
          <Grid container>
            <Grid size={{ xs: 12, md: 9, lg: 8 }}>
              <div className="heroInner">
                {/* Subtitle / Location */}
                <span className="heroTagTitle">VAISHNO DEVI · JAMMU & KASHMIR</span>

                {/* Main Heading */}
                <h1 className="heroMainHeading">
                  KATRA TOUR <br />
                  PACKAGES
                </h1>

                {/* Subtext */}
                <p className="heroSubtext">
                  Curated Vaishno Devi yatra itineraries with stay, transfers and helicopter options.
                </p>

                {/* Pill Search / Selector Card */}
                <div className="bannerSearchCard">
                  {/* Location Selector */}
                  <CustomDropdown
                    icon={LocationOn}
                    value={departureCity}
                    options={departureCities}
                    onChange={setDepartureCity}
                    placeholder="Select City"
                  />

                  {/* Duration Selector */}
                  <CustomDropdown
                    icon={Schedule}
                    value={selectedDuration}
                    options={durationOptions}
                    onChange={setSelectedDuration}
                    placeholder="Select Duration"
                  />

                  {/* Search Button */}
                  <button
                    type="button"
                    className="searchBtn"
                    onClick={handleBannerSearch}
                  >
                    <Search className="btnIcon" />
                    <span>Search</span>
                  </button>
                </div>
              </div>
            </Grid>
          </Grid>
        </Container>
      </section>

      {/* ==========================================
          KATRA PACKAGES FOR EVERY YATRI
      ========================================== */}
      <section className="katraPackagesSection pb-0" id="katraPackagesSection">
        <Container maxWidth="lg">
          {/* Section Header */}
          <div className="sectionTitleBlock">
            <h2>Katra packages for every yatri</h2>
            <p>
              Handpicked itineraries starting from Katra, the gateway to <br />
              Mata Vaishno Devi
            </p>
          </div>

          {/* Cards Grid */}
          <Grid container spacing={3}>
            {katraPackages.map((pkg) => (
              <Grid size={{ xs: 12, md: 4, sm: 6 }} key={pkg.id}>
                <div className="katraPackageCard">
                  {/* Top Image Container */}
                  <div className="cardImgBox">
                    <img src={pkg.image} alt={pkg.title} />

                    {/* Recommended Tag */}
                    {pkg.recommended && (
                      <span className="recommendedTag">Recommended</span>
                    )}

                    {/* Stay Duration Tag */}
                    <span className="stayTag">{pkg.stayTag}</span>
                  </div>

                  {/* Card Content */}
                  <div className="cardContentBox">
                    {/* Meta info */}
                    <span className="metaBadge">{pkg.meta}</span>

                    {/* Title */}
                    <h3 className="pkgTitle">{pkg.title}</h3>

                    {/* Description */}
                    <p className="pkgDesc">{pkg.desc}</p>

                    {/* Inclusions Pills */}
                    <div className="inclusionPills">
                      {pkg.inclusions.map((item, idx) => (
                        <span className="pill" key={idx}>
                          {item}
                        </span>
                      ))}
                    </div>

                    {/* Bottom Pricing & CTA */}
                    <div className="cardBottomWrap">
                      <span className="startingFromLabel">Starting from</span>
                      <div className="priceRow">
                        <span className="oldPrice">{pkg.oldPrice}</span>
                        <span className="finalPrice">{pkg.price}</span>
                      </div>
                      <span className="sharingNote">{pkg.priceNote}</span>

                      <button
                        type="button"
                        className="bookNowBtn"
                        onClick={() => handleBookNow(pkg.title)}
                      >
                        Book Now
                      </button>
                    </div>
                  </div>
                </div>
              </Grid>
            ))}
          </Grid>
        </Container>
      </section>

      {/* ==========================================
          WHAT IS USUALLY INCLUDED & ENQUIRY SECTION
      ========================================== */}
      <section className="inclusionsEnquirySection pb-0" id="enquirySection">
        <Container maxWidth="lg">
          <Grid container spacing={2} alignItems="center">
            {/* Left Side: What is usually included */}
            <Grid size={{ xs: 12, md: 5, lg: 7.2 }}>
              <div className="inclusionsContent">
                <h2 className="inclusionsHeading">What is usually included</h2>
                <p className="inclusionsIntro">
                  Organised Vaishno Devi itineraries are designed to keep the journey
                  smooth, so devotees can focus on prayer and darshan rather than travel
                  planning.
                </p>

                {/* 2-Column Exact Layout */}
                <div className="inclusionsColumns">
                  {/* Left sub-column */}
                  <div className="inclusionsCol">
                    <div className="inclusionItem">
                      <span className="bulletDot" />
                      <span className="inclusionText">
                        Accommodation in hotels <br />
                        at Katra
                      </span>
                    </div>

                    <div className="inclusionItem">
                      <span className="bulletDot" />
                      <span className="inclusionText">
                        Yatra assistance and <br />
                        registration help
                      </span>
                    </div>

                    <div className="inclusionItem">
                      <span className="bulletDot" />
                      <span className="inclusionText">
                        Helicopter tickets (where <br />
                        applicable)
                      </span>
                    </div>

                    <div className="inclusionItem">
                      <span className="bulletDot" />
                      <span className="inclusionText">
                        On-ground support <br />
                        during the yatra
                      </span>
                    </div>
                  </div>

                  {/* Right sub-column */}
                  <div className="inclusionsCol">
                    <div className="inclusionItem">
                      <span className="bulletDot" />
                      <span className="inclusionText">
                        Transfers and local transport
                      </span>
                    </div>

                    <div className="inclusionItem">
                      <span className="bulletDot" />
                      <span className="inclusionText">
                        Sightseeing as per the itinerary
                      </span>
                    </div>

                    <div className="inclusionItem">
                      <span className="bulletDot" />
                      <span className="inclusionText">
                        Meals as specified
                      </span>
                    </div>

                    <div className="inclusionItem">
                      <span className="bulletDot" />
                      <span className="inclusionText">
                        All applicable taxes
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </Grid>

            {/* Right Side: Send an enquiry card */}
            <Grid size={{ xs: 12, md: 5, lg: 4.8 }}>
              <div className="enquiryCardBox">
                <h3 className="enquiryTitle">Send an enquiry</h3>
                <p className="enquirySubtitle">
                  Share your details and our yatra desk will call you back.
                </p>

                {!enquirySubmitted ? (
                  <form onSubmit={handleEnquirySubmit} className="enquiryForm">
                    {enquiryError && (
                      <div className="errorAlert">{enquiryError}</div>
                    )}

                    <div className="formGroup">
                      <input
                        type="text"
                        placeholder="Your name"
                        value={enquiryName}
                        onChange={(e) => setEnquiryName(e.target.value)}
                      />
                    </div>

                    <div className="formGroup">
                      <input
                        type="tel"
                        placeholder="Mobile number"
                        value={enquiryPhone}
                        onChange={(e) => setEnquiryPhone(e.target.value)}
                      />
                    </div>

                    <div className="formGroup">
                      <input
                        type="text"
                        placeholder="Package"
                        value={enquiryPackage}
                        onChange={(e) => setEnquiryPackage(e.target.value)}
                      />
                    </div>

                    <div className="formGroup">
                      <textarea
                        rows="4"
                        placeholder="Travel dates, number of yatris..."
                        value={enquiryNotes}
                        onChange={(e) => setEnquiryNotes(e.target.value)}
                      />
                    </div>

                    <button type="submit" className="submitBtn">
                      Submit enquiry
                    </button>
                  </form>
                ) : (
                  <div className="submittedState">
                    <CheckCircle className="checkIcon" />
                    <h4>Thank You, {enquiryName}!</h4>
                    <p>
                      Your enquiry for <strong>{enquiryPackage}</strong> has been received.
                      Our yatra desk will call you shortly on <strong>{enquiryPhone}</strong>.
                    </p>
                    <button
                      type="button"
                      className="resetBtn"
                      onClick={() => {
                        setEnquirySubmitted(false);
                        setEnquiryName('');
                        setEnquiryPhone('');
                        setEnquiryNotes('');
                      }}
                    >
                      Send Another Enquiry
                    </button>
                  </div>
                )}
              </div>
            </Grid>
          </Grid>
        </Container>
      </section>

      {/* ==========================================
          VAISHNO DEVI TOUR PACKAGES INFO SECTION
      ========================================== */}
      <section className="vdpInfoSection pb-0">
        <Container maxWidth="lg">
          <div className="infoSectionHeader">
            <h2 className="infoSectionTitle">Vaishno Devi tour packages</h2>
            <p className="infoSectionLead">
              Some journeys are planned months in advance, like the Katra tour packages that tend to sell out. Others wait patiently in the background for years — a conversation with family, a story shared by grandparents, a prayer offered during a difficult period. Long before travel dates are discussed, the destination already occupies an important place in the thoughts of many devotees.
            </p>
          </div>

          <Grid container spacing={{ xs: 3, md: 5 }}>
            <Grid size={{ xs: 12, md: 6 }}>
              <div className="infoBlock">
                <h3 className="infoBlockTitle">Beginning the journey from Katra</h3>
                <p className="infoBlockText">
                  For most pilgrims, Katra is the gateway to the shrine. The town has grown around the needs of devotees while keeping its religious identity intact — hotels, guest houses, local markets and yatra facilities support travellers through the year. A well-planned Katra package lets you focus on the spiritual side of the journey instead of the arrangements around it.
                </p>
              </div>
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <div className="infoBlock">
                <h3 className="infoBlockTitle">Bhakti Darshan: the traditional route</h3>
                <p className="infoBlockText">
                  Many devotees still prefer walking to the Bhawan. The changing landscape, the mountain air and the sight of fellow pilgrims moving steadily upward all become part of the experience. For many, the most memorable moments come before the darshan itself — a conversation on the road, a prayer at a resting point, the first distant view of the illuminated Bhawan after sunset.
                </p>
              </div>
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <div className="infoBlock">
                <h3 className="infoBlockTitle">When convenience matters</h3>
                <p className="infoBlockText">
                  Senior citizens, families with young children and travellers with limited time often face practical challenges. Helicopter-assisted yatras keep the spiritual goal unchanged while making the journey easier to manage. The appeal is practical rather than luxurious — only part of the route becomes simpler.
                </p>
              </div>
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <div className="infoBlock">
                <h3 className="infoBlockTitle">A pilgrimage for every generation</h3>
                <p className="infoBlockText">
                  It is common to see grandparents, parents and children travelling together to Vaishno Devi. Many devotees first visit as children and return years later with their own families. That sense of continuity is often the most meaningful part of a Mata Vaishno Devi package.
                </p>
              </div>
            </Grid>
          </Grid>
        </Container>
      </section>

      {/* ==========================================
          FAQS ON KATRA TOUR PACKAGES SECTION
      ========================================== */}
      <section className="vdpFaqSection">
        <Container maxWidth="md">
          <h2 className="faqSectionTitle">FAQs on Katra tour packages</h2>

          <div className="faqCard">
            {faqs.map((faq) => {
              const isOpen = openFaq === faq.id;
              return (
                <div
                  key={faq.id}
                  className={`faqItem ${isOpen ? 'open' : ''}`}
                >
                  <button
                    type="button"
                    className="faqQuestionBtn"
                    onClick={() => toggleFaq(faq.id)}
                  >
                    <span className="faqQuestionText">{faq.question}</span>
                    <KeyboardArrowDown
                      className={`faqArrowIcon ${isOpen ? 'rotated' : ''}`}
                    />
                  </button>

                  {isOpen && (
                    <div className="faqAnswerBox">
                      <p className="faqAnswerText">{faq.answer}</p>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </Container>
      </section>
    </div>
  );
}

export default VaishnoDeviPackage;
